import React, { useEffect, useState } from 'react';
import { getAuth, updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { useAuth } from '../hooks/useAuth'; // Custom hook from your implementation
import axios from 'axios';
import '../styles/Account.css'; // Assuming the styles you implemented earlier

const Account = () => {
  const { user, token } = useAuth(); // Get user from the auth context
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [activeTab, setActiveTab] = useState('password');
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionsError, setSessionsError] = useState('');
  const auth = getAuth(); // Get Firebase auth instance

  useEffect(() => {
    if (activeTab !== 'security') {
      return;
    }

    const fetchSessions = async () => {
      setSessionsLoading(true);
      setSessionsError('');
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_SERVER_BASE_URL}/hospital-portal/api/user-activity/my-sessions`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );
        setSessions(response.data.sessions || []);
      } catch (error) {
        setSessionsError(error.response?.data?.error || 'Failed to load active devices');
      } finally {
        setSessionsLoading(false);
      }
    };

    if (token) {
      fetchSessions();
    }
  }, [activeTab, token]);

  const formatLastActivity = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const now = new Date();
    const lastActivity = new Date(timestamp);
    const diffMinutes = Math.floor((now - lastActivity) / (1000 * 60));
    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return lastActivity.toLocaleDateString();
  };

  const formatIPAddress = (ip) => {
    if (!ip) return 'Unknown';
    if (ip === '::1' || ip === '127.0.0.1') return 'localhost';
    if (ip.startsWith('192.168.') || ip.startsWith('10.') || ip.startsWith('172.')) {
      return `${ip} (local)`;
    }
    return ip;
  };

  const parseUserAgent = (userAgent) => {
    if (!userAgent) return 'Unknown Device';
    if (userAgent.includes('iPhone')) return 'iPhone';
    if (userAgent.includes('iPad')) return 'iPad';
    if (userAgent.includes('Android') && userAgent.includes('Mobile')) return 'Android Phone';
    if (userAgent.includes('Android')) return 'Android Tablet';
    if (userAgent.includes('Windows')) return 'Windows PC';
    if (userAgent.includes('Mac OS X') || userAgent.includes('macOS')) return 'Mac';
    if (userAgent.includes('Linux')) return 'Linux';
    return 'Desktop';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');

    if (newPassword !== confirmPassword) {
      setErrorMessage('New password and confirm password do not match');
      return;
    }

    if (newPassword.length < 6) {
      setErrorMessage('Password should be at least 6 characters long');
      return;
    }

    try {
      // Re-authenticate the user
      const credential = EmailAuthProvider.credential(
        auth.currentUser.email,
        currentPassword
      );

      await reauthenticateWithCredential(auth.currentUser, credential);

      // Update password after re-authentication
      await updatePassword(auth.currentUser, newPassword);

      setSuccessMessage('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      setErrorMessage(error.message);
    }
  };

  return (
    <div className="account-container">
      <h2>Account</h2>

      <div className="account-tabs">
        <button
          className={`account-tab ${activeTab === 'password' ? 'active' : ''}`}
          onClick={() => setActiveTab('password')}
          type="button"
        >
          Password
        </button>
        <button
          className={`account-tab ${activeTab === 'security' ? 'active' : ''}`}
          onClick={() => setActiveTab('security')}
          type="button"
        >
          Security
        </button>
      </div>

      {activeTab === 'password' && (
        <form onSubmit={handleSubmit}>
          <div>
            <label>Current Password:</label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
            />
          </div>
          <div>
            <label>New Password:</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
            />
          </div>
          <div>
            <label>Confirm New Password:</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
          </div>
          {errorMessage && <div className="error-message">{errorMessage}</div>}
          {successMessage && <div className="success-message">{successMessage}</div>}
          <button type="submit">Change Password</button>
        </form>
      )}

      {activeTab === 'security' && (
        <div className="security-panel">
          <h3>Active Devices</h3>
          <p className="security-subtitle">Devices currently logged into your account</p>

          {sessionsLoading && <div className="loading-message">Loading devices...</div>}
          {sessionsError && <div className="error-message">{sessionsError}</div>}

          {!sessionsLoading && !sessionsError && sessions.length === 0 && (
            <div className="empty-message">No active sessions found.</div>
          )}

          {!sessionsLoading && !sessionsError && sessions.length > 0 && (
            <div className="device-list">
              {sessions.map((session) => (
                <div key={session.session_id} className="device-card">
                  <div className="device-main">
                    <span className="device-name">{parseUserAgent(session.user_agent)}</span>
                    <span className="device-last-active">Last active {formatLastActivity(session.last_activity)}</span>
                  </div>
                  <div className="device-meta">
                    <span className="device-ip">{formatIPAddress(session.ip_address)}</span>
                    <span className="device-login">Logged in {formatLastActivity(session.login_time)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
  
};

export default Account;
